import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ReferenceTableService {

  constructor(private http: HttpClient) {}

  // CRITICAL: Accept filterParams and rowData parameters
  saveReferenceTableForm(
    baseUrl: string,
    saveData: any,
    globalService: any,
    filterParams: any,
    rowData: any,
    settingsObj: any
  ): Observable<any> {
    // Determine if this is update or create based on ACTION or object_id
    const isUpdate = saveData.ACTION === 'UPDATE' || saveData.object_id || rowData?.object_id;
    
    // Use different action codes for create vs update
    const actionCode = isUpdate ? 'ji0byt' : '55rzd5';
    const url = `${baseUrl}/saveJsonObject?action_code=${actionCode}`;

    // Map FROM_TAX_YEAR to FROM_YEAR and TO_TAX_YEAR to TO_YEAR
    const fromYear = saveData.FROM_TAX_YEAR || saveData.FROM_YEAR || '';
    const toYear = saveData.TO_TAX_YEAR || saveData.TO_YEAR || '';
    const taxYear = filterParams?.tax_year ?? globalService?.tax_year ?? saveData.TAX_YEAR ?? rowData?.TAX_YEAR;
    
    // Build jsonSettings object
    const jsonSettings = {
      tax_year: taxYear
    };
    
    // 🚀 CRITICAL: Build json_data differently for create vs update
    let jsonData: any;
    if (isUpdate) {
      // Update mode: single object with additional fields (action_code ji0byt)
      // Construct LAST_UPDATED_DATE structure to match expected payload
      const lastUpdatedDate = saveData.LAST_UPDATED_DATE || rowData?.LAST_UPDATED_DATE || {
        timeZone: "America/New_York",
        length: 13,
        bytes: "eH0KHQwZHAAAAACBkA==",
        null: false,
        stream: {}
      };
      
      jsonData = {
        COLUMN_NAME: saveData.TABLE_NAME || '',
        REF_CODE: saveData.REF_CODE || '',
        REF_VALUE: saveData.REF_VALUE || '',
        FROM_YEAR: fromYear,
        TO_YEAR: toYear,
        LAST_UPDATED_BY: saveData.LAST_UPDATED_BY || globalService?.sso_id || filterParams?.sso_id || '',
        LAST_UPDATED_DATE: lastUpdatedDate,
        object_id: saveData.object_id || rowData?.object_id || '',
        SCENARIO_TYPE_CODE: saveData.SCENARIO_TYPE_CODE || '',
        tax_year: taxYear
      };
    } else {
      // Create mode: array format (action_code 55rzd5)
      jsonData = [{
        COLUMN_NAME: saveData.TABLE_NAME || '',
        REF_CODE: saveData.REF_CODE || '',
        REF_VALUE: saveData.REF_VALUE || '',
        FROM_YEAR: fromYear,
        TO_YEAR: toYear
      }];
    }
    
    // Build checkLockedbj with correct structure
    const checkLockedbj = [{
      scenario_key: String(filterParams?.scenario ?? globalService?.scenario ?? rowData?.SCENARIO ?? '')
    }];
    
    // Build the complete params object exactly as specified
    const params: any = {
      jsonSettings: JSON.stringify(jsonSettings),
      json_data: JSON.stringify(jsonData),
      tax_year: taxYear,
      screen_key: filterParams?.screen_key || globalService?.screen_key || saveData.SCREEN_KEY || '',
      scenario: filterParams?.scenario ?? globalService?.scenario ?? saveData.SCENARIO ?? rowData?.SCENARIO,
      jcd_key: filterParams?.jcd_key ?? globalService?.jcd_key ?? saveData.JCD_KEY ?? rowData?.JCD_KEY,
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || saveData.JCD_TA_KEY || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || saveData.ACTIVITY_KEY || '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: filterParams?.issue_id || globalService?.issue_id || rowData?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || globalService?.issue_short_desc || rowData?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || rowData?.issue_id_for_tx_save || '',
      is_issue_key_enabled: 'N',
      scenarioCode: filterParams?.scenarioCode || globalService?.scenarioCode || filterParams?.scenario_code || globalService?.scenario_code || 'RAF',
      checkLockedbj: JSON.stringify(checkLockedbj)
    };

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // CRITICAL: Delete reference table entry with simplified payload
  deleteReferenceTableForm(
    baseUrl: string,
    saveData: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=qfe2kq`;

    // Build checkLockedbj with correct structure (scenario_key as string)
    const checkLockedbj = [{
      scenario_key: String(filterParams?.scenario ?? globalService?.scenario ?? rowData?.SCENARIO ?? '')
    }];
    
    // Build the complete params object exactly as specified
    const params: any = {
      column_name: saveData.TABLE_NAME || '',
      ref_code: saveData.REF_CODE || '',
      tax_year: saveData.TAX_YEAR ?? filterParams?.tax_year ?? globalService?.tax_year ?? rowData?.TAX_YEAR,
      screen_key: saveData.SCREEN_KEY || filterParams?.screen_key || globalService?.screen_key || '',
      scenario: saveData.SCENARIO ?? filterParams?.scenario ?? globalService?.scenario ?? rowData?.SCENARIO,
      jcd_key: saveData.JCD_KEY ?? filterParams?.jcd_key ?? globalService?.jcd_key ?? rowData?.JCD_KEY,
      jcd_ta_key: saveData.JCD_TA_KEY || filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '',
      activity_key: saveData.ACTIVITY_KEY || filterParams?.activity_key || globalService?.activity_key || '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: filterParams?.issue_id || globalService?.issue_id || rowData?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || globalService?.issue_short_desc || rowData?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || rowData?.issue_id_for_tx_save || '',
      scenarioCode: filterParams?.scenarioCode || globalService?.scenarioCode || filterParams?.scenario_code || globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: 'N',
      checkLockedbj: JSON.stringify(checkLockedbj)
    };

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // Load reference table data - loads distinct column names
  // Based on referenceTableActionsService.js - uses action_code 'uy8nox' (loadAllColumnNames)
  // Maps to action_id 32791 (GET_DIST_COL_NAMES)
  loadReferenceTableData(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const baseParams: any = {
      action_id: 32791,
      action_code: 'uy8nox',
      tax_year: filterParams?.tax_year || globalService?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || globalService?.scenario || rowData?.scenario,
      scenario_code: filterParams?.scenario_code || globalService?.scenario_code || rowData?.scenario_code,
      table_name: '',
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      is_issue_key_enabled: filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || rowData?.is_issue_key_enabled,
      issue_short_desc: '',
      jcd_key: globalService?.jcd_key || rowData?.JCD_KEY || '',
      jcd_ta_key: globalService?.jcd_ta_key || '',
      screen_key: globalService?.screen_key || '',
      screen_type: 'grid'
    };

    // Build checkLockedbj from real rowData
    const checkLockedbj = [{
      leid: rowData?.LEID || rowData?.SOURCE_LEID,
      cdr_number: rowData?.CDR_NO || rowData?.TAX_CODE,
      consol_group_key: String(rowData?.CONSOL_GROUP_KEY || ''),
      reporting_period: String(rowData?.REPORTING_PERIOD || ''),
      combination_key: String(rowData?.COMBINATION_KEY || ''),
      scenario_key: parseInt(filterParams?.scenario || 0)
    }];
    baseParams.checkLockedbj = JSON.stringify(checkLockedbj);

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url);
  }

  // Load reference table by name - loads distinct ref codes for a given column_name/table_name
  // Based on referenceTableActionsService.js - uses action_code 'wvcqdd' (loadAllRefCode)
  // Maps to action_id 32793 (GET_DIST_REF_CODE)
  loadReferenceTableByName(
    baseUrl: string,
    tableName: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const baseParams: any = {
      action_id: 32793,
      action_code: 'wvcqdd',
      column_name: tableName,
      tax_year: filterParams?.tax_year || globalService?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || globalService?.scenario || rowData?.scenario,
      scenario_code: filterParams?.scenario_code || globalService?.scenario_code || rowData?.scenario_code,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: globalService?.jcd_key || rowData?.JCD_KEY || '',
      jcd_ta_key: globalService?.jcd_ta_key || '',
      screen_key: globalService?.screen_key || '',
      screen_type: 'grid'
    };

    // Build checkLockedbj from real rowData
    const checkLockedbj = [{
      leid: rowData?.LEID || rowData?.SOURCE_LEID,
      cdr_number: rowData?.CDR_NO || rowData?.TAX_CODE,
      consol_group_key: String(rowData?.CONSOL_GROUP_KEY || ''),
      reporting_period: String(rowData?.REPORTING_PERIOD || ''),
      combination_key: String(rowData?.COMBINATION_KEY || ''),
      scenario_key: parseInt(filterParams?.scenario || 0)
    }];
    baseParams.checkLockedbj = JSON.stringify(checkLockedbj);

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url);
  }
}
